// src/App.js
import React, { useState, useEffect } from "react";
import { useModel } from "./hooks/useModel";
import "./App.css";
import InvestmentChart from "./components/InvestmentChart";
import EfficiencyByAreaChart from "./components/EfficiencyByAreaChart";
import EfficiencyByRentChart from "./components/EfficiencyByRentChart";
import UsnComparisonChart from "./components/UsnComparisonChart";
import PaybackChart from "./components/PaybackChart";
import InvestmentTable from "./components/InvestmentTable";
import ProfitLossTable from "./components/ProfitLossTable";
import CashFlowTable from "./components/CashFlowTable";
import TextBlock from "./components/TextBlock";
import Logo from "./components/Logo";
import BitrixForm from "./components/BitrixForm";
import ExplanationsRight from "./components/ExplanationsRight";
import Footer from "./components/Footer";
import SalesTable from "./components/SalesTable";
import SalesBoxplot from "./components/SalesBoxplot";
import NavMenu from "./components/NavMenu";

function App() {
  // Состояния
  const [square, setSquare] = useState(700);
  const [rentalRate, setRentalRate] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);
  const [loanPeriod, setLoanPeriod] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [checkbox, setCheckbox] = useState(false);
  const [keyRate, setKeyRate] = useState(0.17);

  useEffect(() => {
    fetch("/src/data/key_rate.json")
      .then((res) => {
        if (!res.ok) throw new Error("Ошибка загрузки");
        return res.json();
      })
      .then((data) => setKeyRate(data.value / 100))
      .catch(() => setKeyRate(0.17));
  }, []);

  const model = useModel(
    square,
    rentalRate,
    loanAmount,
    interestRate / 100,
    loanPeriod,
    checkbox,
    keyRate,
  );

  if (!model) return <div className="App">Загрузка данных...</div>;
  const maxLoan = model.sumInvestments;

  return (
    <div className="App">
      <NavMenu />
      {/* ========== СЕКЦИЯ ВВОДА ДАННЫХ ========== */}
      <section className="top-section" id="inputData">
        <div className="container-left">
          <div className="input">
            <h1>ВВЕДИТЕ ИСХОДНЫЕ ДАННЫЕ</h1>
            <div className="input-area">
              <label>Площадь помещения, м²:</label>
              <input
                type="range"
                min="700"
                max="2500"
                step="1"
                value={square}
                onChange={(e) => setSquare(Number(e.target.value))}
              />
              <span>{square}</span>
            </div>
            <div className="input-area">
              <label>Арендная ставка, руб/м² в месяц:</label>
              <input
                type="range"
                min="0"
                max="1500"
                step="10"
                value={rentalRate}
                onChange={(e) => setRentalRate(Number(e.target.value))}
              />
              <span>{rentalRate}</span>
            </div>
            <div className="input-area">
              <label>Сумма кредита, руб:</label>
              <input
                type="range"
                min="0"
                max={maxLoan}
                step="100000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
              />
              <span>{loanAmount.toLocaleString("ru-RU")}</span>
            </div>
            <div className="input-area">
              <label>Срок кредита, мес:</label>
              <input
                type="range"
                min="0"
                max="84"
                step="1"
                value={loanPeriod}
                onChange={(e) => setLoanPeriod(Number(e.target.value))}
              />
              <span>{loanPeriod}</span>
            </div>
            <div className="input-area">
              <label>Ставка по кредиту, % годовых:</label>
              <input
                type="range"
                min="0"
                max="30"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
              />
              <span>{interestRate}%</span>
            </div>
            <div className="checkbox-area" style={{ display: "none" }}>
              <label>Учитывать амортизацию:</label>
              <input
                type="checkbox"
                checked={checkbox}
                onChange={(e) => setCheckbox(e.target.checked)}
              />
            </div>
          </div>
        </div>
        <div className="container-right">
          <h1>ПОКАЗАТЕЛИ МОДЕЛИ</h1>
          <div className="out" id="outData">
            <div className="out-area">
              <p>
                СУММА ВЛОЖЕНИЙ{" "}
                <span className="value-group">
                  <span>
                    {model.sumInvestments.toLocaleString("ru-RU")} руб
                  </span>
                </span>
              </p>
            </div>
            <div className="out-area">
              <p>
                Эффективность вложений (прогноз){" "}
                <span className="value-group">
                  <span>
                    {model.meanProfitabilityI >= 0
                      ? (model.meanProfitabilityI * 100).toFixed(0) + "%"
                      : "убыток"}
                  </span>
                </span>
              </p>
            </div>
            <div className="out-area">
              <p>
                Собственные средства (нужно){" "}
                <span className="value-group">
                  <span>
                    {Math.round(model.OwnerContribution).toLocaleString(
                      "ru-RU",
                    )}{" "}
                    руб
                  </span>
                </span>
              </p>
            </div>
            <div className="out-area">
              <p>
                Расчетный срок окупаемости{" "}
                <span className="value-group">
                  <span
                    className={
                      typeof model.paybackPeriodYear === "string"
                        ? "red"
                        : "black"
                    }
                  >
                    {typeof model.paybackPeriodYear === "number"
                      ? model.paybackPeriodYear + " года"
                      : model.paybackPeriodYear}
                  </span>
                </span>
              </p>
            </div>
            <div className="out-area">
              <p>
                Ожидаемый прирост за 7 лет{" "}
                <span className="value-group">
                  <span
                    className={
                      (model.ccf?.ccf[0][7] || 0) < 0 ? "red" : "black"
                    }
                  >
                    {Math.round(model.ccf?.ccf[0][7] || 0).toLocaleString(
                      "ru-RU",
                    )}{" "}
                    руб
                  </span>
                </span>
              </p>
            </div>
            <div className="out-area">
              <p>
                Оценка риска кассовый разрыв{" "}
                <span className="value-group">
                  <span
                    className={
                      model.cashGap !== "НЕТ" && model.cashGap < 0
                        ? "red"
                        : "black"
                    }
                  >
                    {model.cashGap === "НЕТ"
                      ? model.cashGap
                      : model.cashGap.toLocaleString("ru-RU") + " руб"}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ФИНАНСОВЫЕ РЕЗУЛЬТАТЫ ========== */}
      <section className="top-section" id="financialResults">
        <ProfitLossTable model={model} />
      </section>

      {/* ========== ИНВЕСТИЦИИ ========== */}
      <section className="top-section" id="investment">
        <div className="container-left">
          <InvestmentTable
            investment={model.investment}
            sumInvestments={model.sumInvestments}
            square={square}
          />
        </div>
        <div className="container-right">
          <InvestmentChart />
          <div className="text-block">
            <p>
              На графике «Сумма вложений от площади» представлена зависимость
              объема необходимых вложений от площади помещения...
            </p>
            <p>
              Нижняя граница в 700 м² определена нашими специалистами как
              минимальная площадь для сохранения формата клуба без потери
              качества услуг.
            </p>
            <p>
              Для выбора оптимальной площади рекомендуем проанализировать{" "}
              <a href="#analyticsCharts" className="anchor-link">
                график «Анализ эффективности вложений от площади»
              </a>
              . Наиболее эффективные показатели наблюдаются в диапазоне от 900
              до 1300 м².
            </p>
          </div>
        </div>
      </section>

      {/* ========== ГРАФИКИ ========== */}
      <section className="top-section" id="analyticsСharts">
        <div className="container-left">
          <EfficiencyByAreaChart
            rentalRate={rentalRate}
            loanAmount={loanAmount}
            interestRate={interestRate}
            loanPeriod={loanPeriod}
            checkbox={checkbox}
            keyRate={keyRate}
            discountRate={model.discountRate}
          />
          <UsnComparisonChart
            square={square}
            rentalRate={rentalRate}
            loanAmount={loanAmount}
            interestRate={interestRate}
            loanPeriod={loanPeriod}
            checkbox={checkbox}
            keyRate={keyRate}
          />
        </div>
        <div className="container-right">
          <EfficiencyByRentChart
            square={square}
            loanAmount={loanAmount}
            interestRate={interestRate}
            loanPeriod={loanPeriod}
            checkbox={checkbox}
            keyRate={keyRate}
            discountRate={model.discountRate}
          />
          <PaybackChart
            square={square}
            rentalRate={rentalRate}
            loanAmount={loanAmount}
            interestRate={interestRate}
            loanPeriod={loanPeriod}
            checkbox={checkbox}
            keyRate={keyRate}
          />
        </div>
      </section>
      {/* ========== ДДС ========== */}
      {model.operating &&
        model.credit &&
        model.inv &&
        model.cf &&
        model.ccf && (
          <section className="top-section" id="cashFlowSection">
            <CashFlowTable
              ccf={model.ccf}
              operating={model.operating}
              credit={model.credit}
              inv={model.inv}
              cf={model.cf}
            />
          </section>
        )}

      {/* ========== КАРТЫ ========== */}
      <section className="top-section" id="lastElem">
        <div className="container-left">
          <SalesTable
            headSalesCards={model.headSalesCards}
            saleCards={model.saleCards}
          />
        </div>
        <div className="container-right">
          <SalesBoxplot
            modelSales={Object.values(model.saleCards).map((v) => v[4])}
          />
        </div>
      </section>

      {/* ========== ОПИСАНИЕ И КОНТАКТЫ ========== */}
      <section className="top-section" id="lastElem2">
        <div className="container-left">
          <TextBlock model={model} />
          <Logo />
        </div>
        <div className="container-right">
          <ExplanationsRight />
          <BitrixForm />
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default App;
